import React, { useState, useEffect } from 'react';
import { Modal, Table, Button, Select, Input, Tag, Space, message, Popconfirm, Avatar, Tooltip, Divider, Alert } from 'antd';
import { UserAddOutlined, DeleteOutlined, MailOutlined, CrownOutlined, EditOutlined, EyeOutlined, TeamOutlined, DownloadOutlined } from '@ant-design/icons';
import SurveyService from '../../services/SurveyService';
import * as XLSX from 'xlsx';

const { Option } = Select;

const ROLE_CONFIG = {
    co_owner:          { label: 'Co-Owner',          color: 'gold',   icon: <CrownOutlined/>,  desc: 'Full access — can edit, manage collectors, view analytics' },
    editor:            { label: 'Editor',             color: 'blue',   icon: <EditOutlined/>,   desc: 'Can edit questions and survey settings' },
    collector_manager: { label: 'Collector Manager',  color: 'purple', icon: <TeamOutlined/>,   desc: 'Can manage data collectors only' },
    viewer:            { label: 'Viewer',             color: 'green',  icon: <EyeOutlined/>,    desc: 'Can view analytics and responses only' },
};

// XLSForm export utility
function exportToXLSForm(survey, questions, sections) {
    const typeMap = {
        text: 'text', textarea: 'text', number: 'integer', single: 'select_one',
        multiple: 'select_multiple', dropdown: 'select_one', scale: 'integer',
        boolean: 'select_one', date: 'date', matrix: 'select_one',
    };

    const surveyRows = [['type','name','label','required','hint','relevant','constraint']];
    const choicesRows = [['list_name','name','label']];
    const usedLists = new Set();

    // Add sections as groups
    const secMap = {};
    (sections || []).forEach(s => { secMap[s.id] = s; });

    let lastSec = null; let secCounter = 0; const usedNames = new Set();
    (questions || []).forEach((q, idx) => {
        const secId = q.section_id;
        if (secId && secId !== lastSec) {
            if (lastSec) surveyRows.push(['end group', '', '', '', '', '', '']);
    // Note: end group does not need a name in XLSForm
            const sec = secMap[secId];
            const secSlug = 'grp_sec_' + String(++secCounter);
            surveyRows.push(['begin group', secSlug, sec?.title || 'Section', '', '', '', '']);
            lastSec = secId;
        }

        const xlsType = typeMap[q.type] || 'text';
        const rawName = (q.xlsform_name || ('q_' + (idx+1))).toString();
        const baseName = rawName.replace(/[^a-zA-Z0-9_]/g, '_').replace(/^([0-9])/, 'q_$1').replace(/_+/g,'_').slice(0,60) || ('q_' + (idx+1));
        const name = usedNames.has(baseName) ? (baseName + '_' + (idx+1)) : baseName;
        usedNames.add(name);
        const listName = `list_${name}`;

        let type = xlsType;
        if (['select_one','select_multiple'].includes(xlsType)) {
            type = xlsType + ' ' + listName;
            const opts = typeof q.options === 'string' ? JSON.parse(q.options||'[]') : (q.options||[]);
            opts.forEach((opt, oi) => {
                if (!usedLists.has(listName)) usedLists.add(listName);
                choicesRows.push([listName, `opt_${oi+1}`, opt]);
            });
        }
        if (q.type === 'boolean') {
            type = 'select_one ' + listName;
            if (!usedLists.has(listName)) {
                usedLists.add(listName);
                choicesRows.push([listName, 'yes', 'Yes']);
                choicesRows.push([listName, 'no', 'No']);
            }
        }

        // Skip logic → relevant
        let relevant = '';
        if (q.skip_logic) {
            const sl = typeof q.skip_logic === 'string' ? JSON.parse(q.skip_logic) : q.skip_logic;
            if (sl?.field && sl?.value) {
                const op = sl.operator === '!=' ? '!=' : '=';
                relevant = `\${${sl.field}} ${op} '${sl.value}'`;
            }
        }

        surveyRows.push([type, name, q.label || '', q.required ? 'yes' : 'no', q.helper_text || '', relevant, '']);
    });

    if (lastSec) surveyRows.push(['end group', '', '', '', '', '', '']);
    // Note: end group does not need a name in XLSForm

    const settingsRows = [
        ['form_title', 'form_id', 'version'],
        [survey.title, `survey_${survey.id}`, new Date().toISOString().slice(0,10).replace(/-/g,'')]
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(surveyRows),   'survey');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(choicesRows),  'choices');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(settingsRows), 'settings');
    XLSX.writeFile(wb, `${(survey.title||'survey').replace(/[^a-z0-9]/gi,'_')}_xlsform.xlsx`);
}

export default function QoCSurveyCollaborators({ survey, onClose }) {
    const [collaborators, setCollaborators] = useState([]);
    const [loading,       setLoading]       = useState(false);
    const [inviteEmail,   setInviteEmail]   = useState('');
    const [inviteRole,    setInviteRole]    = useState('viewer');
    const [inviting,      setInviting]      = useState(false);
    const [exporting,     setExporting]     = useState(false);

    useEffect(() => { load(); }, [survey?.id]); // eslint-disable-line

    const load = async () => {
        if (!survey?.id) return;
        setLoading(true);
        try {
            const res = await SurveyService.getCollaborators(survey.id);
            setCollaborators(res.data?.data || []);
        } catch(e) { message.error('Failed to load collaborators'); }
        finally { setLoading(false); }
    };

    const handleInvite = async () => {
        if (!inviteEmail) { message.error('Enter an email address'); return; }
        setInviting(true);
        try {
            const res = await SurveyService.inviteCollaborator(survey.id, { email: inviteEmail, role: inviteRole });
            if (res.data?.code === 200) {
                message.success(res.data.message);
                setInviteEmail('');
                load();
            } else {
                message.error(res.data?.message || 'Failed to invite');
            }
        } catch(e) { message.error(e.message || 'Failed to invite'); }
        finally { setInviting(false); }
    };

    const handleRoleChange = async (collaboratorId, role) => {
        try {
            await SurveyService.updateCollaboratorRole(collaboratorId, { role });
            message.success('Role updated');
            load();
        } catch(e) { message.error('Failed to update role'); }
    };

    const handleRemove = async (collaboratorId) => {
        try {
            await SurveyService.removeCollaborator(collaboratorId);
            message.success('Removed');
            load();
        } catch(e) { message.error('Failed to remove'); }
    };

    const handleXLSFormExport = async () => {
        setExporting(true);
        try {
            const res = await SurveyService.exportXLSForm(survey.id);
            const { survey: sv, questions, sections } = res.data?.data || {};
            exportToXLSForm(sv || survey, questions || [], sections || []);
            message.success('XLSForm downloaded');
        } catch(e) { message.error('Export failed: ' + e.message); }
        finally { setExporting(false); }
    };

    const cols = [
        {
            title: 'Collaborator', render: (_, r) => (
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <Avatar style={{ background: '#1890ff', fontSize:12 }}>
                        {(r.user_name || r.invited_email || '?')[0].toUpperCase()}
                    </Avatar>
                    <div>
                        <div style={{ fontWeight:600, fontSize:13 }}>
                            {r.user_name || r.invited_email}
                        </div>
                        <div style={{ fontSize:11, color:'#888' }}>{r.user_email || r.invited_email}</div>
                    </div>
                    {!r.accepted && (
                        <Tag color="orange" style={{ fontSize:10 }}>Pending</Tag>
                    )}
                </div>
            )
        },
        {
            title: 'Role', width: 200,
            render: (_, r) => (
                <Select value={r.role} size="small" style={{ width:180 }}
                    onChange={v => handleRoleChange(r.id, v)}>
                    {Object.entries(ROLE_CONFIG).map(([k, v]) => (
                        <Option key={k} value={k}>
                            <Tooltip title={v.desc}>
                                <span>{v.icon} <Tag color={v.color} style={{ fontSize:10, marginLeft:4 }}>{v.label}</Tag></span>
                            </Tooltip>
                        </Option>
                    ))}
                </Select>
            )
        },
        {
            title: '', width: 60,
            render: (_, r) => (
                <Popconfirm title="Remove this collaborator?" onConfirm={() => handleRemove(r.id)}>
                    <Button size="small" danger type="text" icon={<DeleteOutlined/>}/>
                </Popconfirm>
            )
        }
    ];

    return (
        <div>
            {/* XLSForm Export */}
            <div style={{ background:'#f0f9ff', border:'1px solid #bae6fd', borderRadius:8, padding:'12px 16px', marginBottom:20, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <div>
                    <div style={{ fontWeight:700, fontSize:13, color:'#0369a1' }}>📱 KoboCollect / ODK Export</div>
                    <div style={{ fontSize:12, color:'#64748b', marginTop:2 }}>Download this survey as an XLSForm to use in KoboCollect or ODK Collect</div>
                </div>
                <Button icon={<DownloadOutlined/>} loading={exporting} onClick={handleXLSFormExport}
                    style={{ borderColor:'#0369a1', color:'#0369a1' }}>
                    Download XLSForm
                </Button>
            </div>

            <Divider orientation="left" style={{ fontSize:13, fontWeight:700 }}>Collaborators</Divider>

            {/* Invite form */}
            <div style={{ background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:8, padding:16, marginBottom:16 }}>
                <div style={{ fontWeight:700, fontSize:13, marginBottom:10 }}>
                    <UserAddOutlined style={{ marginRight:6 }}/>Invite Collaborator
                </div>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                    <Input prefix={<MailOutlined style={{ color:'#94a3b8' }}/>}
                        placeholder="Email address (registered or new user)"
                        value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
                        onPressEnter={handleInvite}
                        style={{ flex:2, minWidth:220 }}/>
                    <Select value={inviteRole} onChange={setInviteRole} style={{ width:180 }}>
                        {Object.entries(ROLE_CONFIG).map(([k, v]) => (
                            <Option key={k} value={k}>
                                {v.icon} <span style={{ marginLeft:4 }}>{v.label}</span>
                            </Option>
                        ))}
                    </Select>
                    <Button type="primary" loading={inviting} onClick={handleInvite} icon={<UserAddOutlined/>}>
                        Invite
                    </Button>
                </div>
                <Alert type="info" showIcon style={{ marginTop:10, fontSize:11 }}
                    message="Non-registered users will receive an email to create a general account and join this survey."/>
            </div>

            {/* Collaborators table */}
            <Table rowKey="id" dataSource={collaborators} columns={cols}
                loading={loading} size="small" pagination={false}
                locale={{ emptyText: 'No collaborators yet — invite someone above' }}/>
        </div>
    );
}






